import { Box, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { TertiaryButton } from 'components/lib/TertiaryButton';
import theme from 'providers/theme/theme';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PurchaseNavigationButtonMobileStyle } from 'routes/Purchase/Purchase.styles';

import { FilloutStandardEmbed } from '@fillout/react';
import { useSubmission } from 'api/submission';
import type { FilloutFormProps, FilloutFormRef } from './FilloutForm.types';

const filloutId = import.meta.env.REACT_APP_CONSUMER_FILLOUT_FORM_ID || '';

const FilloutForm = forwardRef<FilloutFormRef | null, FilloutFormProps>(
  ({ purchaseNavigationButtonHeight }, ref) => {
    const { id } = useParams();
    const { data: submission } = useSubmission(String(id));
    const ql = submission?.quote_locator || '';
    const navigate = useNavigate();

    const isMobileLayout = useMediaQuery(theme.breakpoints.down('sm'));

    const filloutFormBoxRef = useRef<HTMLDivElement>(null);
    const [filloutFormBoxHeight, setFilloutFormBoxHeight] = useState<number | null>(null);

    const [isFormSent, setIsFormSent] = useState(false);

    // Set height of filloutFormBoxRef when it will be mounted
    useEffect(() => {
      if (filloutFormBoxRef.current) {
        const { height } = filloutFormBoxRef.current.getBoundingClientRect();
        setFilloutFormBoxHeight(height);
      }
    }, []);

    const onPrimaryButtonClick = () => {
      // Status transition to Payment Required already happened when "Finalize and Purchase" was clicked
      // Just navigate to payment page
      navigate(`/submission/${id}/payment`);
    };

    const calculatedHeight =
      isMobileLayout && filloutFormBoxHeight && purchaseNavigationButtonHeight
        ? `${filloutFormBoxHeight - purchaseNavigationButtonHeight + 16}px !important`
        : filloutFormBoxHeight && !isMobileLayout
          ? `${filloutFormBoxHeight}px !important`
          : 'auto';

    return (
      <Stack flex={1} gap={3} justifyContent="space-between">
        <Stack>
          <Typography variant="h1">Finalize your purchase</Typography>

          <Typography variant="body1">
            Please review the document for PIP and provide your signature below.
          </Typography>
        </Stack>

        <Box
          ref={filloutFormBoxRef}
          flex={1}
          sx={{
            '&>div:first-of-type': {
              height: calculatedHeight,
            },
          }}
        >
          <FilloutStandardEmbed
            filloutId={filloutId}
            domain="forms.threeinsurance.com"
            parameters={{
              ql,
            }}
            onSubmit={() => setIsFormSent(true)}
          />
        </Box>

        <Stack sx={isMobileLayout ? PurchaseNavigationButtonMobileStyle : {}} ref={ref}>
          <Tooltip title={isFormSent ? '' : 'Please complete the form to proceed'}>
            <Box>
              <PrimaryButton
                text="Purchase"
                data-test="Purchase"
                onClick={onPrimaryButtonClick}
                disabled={!isFormSent}
              />
            </Box>
          </Tooltip>

          <TertiaryButton
            text="Previous"
            onClick={() => navigate(`/submission/${id}?section=1&step=0`, { replace: true })}
            data-test="Previous"
          />
        </Stack>
      </Stack>
    );
  },
);

export default FilloutForm;
